import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { VehicleV2 } from 'types/VehicleV2';
import { LeadFormFields } from 'types/LeadForm';
import { useLeadSubmitter } from 'lib/hooks/useLeadSender';
import { Features, SourcePages } from 'lib/EventSchema';
import { captureException } from '@sentry/nextjs';
import * as _ from 'lodash-es';
import { LeadTrackingPageSource } from 'types/Apis/LeadTrackingParams';
import { useAutolistUser } from 'lib/hooks/useAutolistUser';
import useDeviceScreen from 'lib/hooks/useDeviceScreen';
import { useAlert } from 'lib/hooks/useAlert';
import useSubmitted from 'lib/hooks/useSubmitted';
import { trackLeadSuccess } from 'lib/Tracking/LeadForm/FormSuccess';
import FormManager, { SubmissionState } from 'components/LeadForm/FormManager';
import css from 'styled-jsx/css';
import classNames from 'classnames';
import qcLogo from 'public/static/images/qc_logo.svg';
import closeButton from 'public/static/images/srp/Close_Mobile.svg';
import styled from 'styled-components';
import { screen } from 'components/styles/variables';
import SrpEventContext from 'contexts/SrpEventContext';
import IconButton from 'components/Shared/Buttons/IconButton';
import ModalForm from './ModalForm';
import LeadFormTrackingContext from '../Vdp/LeadFormTrackingContext';

const marginCss = css.resolve`
  margin: 0px 16px;
`;

const inputCss = css.resolve`
  .padding {
    padding: 0px 16px;
  }
`;

const CloseIconButton = styled(IconButton)`
  && {
    background-image: url(${closeButton});
    top: 4px;
    right: 5px;
    width: 40px;
    height: 40px;
    padding: 0;
    position: absolute;
    background-size: 16px 16px;
    background-repeat: no-repeat;
    background-position: center;
    background-color: transparent;
    border-style: none;
  }
`;

export interface QuickContactLeadFormProps {
  selectedVehicle: VehicleV2;
  onClose: () => void;
  onSubmitSuccess?: (submittedVehicle: VehicleV2) => void;
}

const QuickContactLeadForm = ({
  selectedVehicle,
  onClose,
  onSubmitSuccess,
}: QuickContactLeadFormProps): JSX.Element => {
  const { t } = useTranslation();
  const { showError } = useAlert();
  const { zip } = useAutolistUser();
  const { isMobile, isTablet } = useDeviceScreen();
  const { sourcePageCategory } = useContext(SrpEventContext);
  const sourcePage =
    isMobile || isTablet ? LeadTrackingPageSource.MobileSrp : LeadTrackingPageSource.DesktopSrp;
  const leadSubmitter = useLeadSubmitter(
    Features.leadForm,
    sourcePage,
    SourcePages.SrpLeadForm,
    sourcePageCategory
  );
  const leadSubmission = useSubmitted(selectedVehicle?.vin);

  async function handleSubmit({ sendAlerts, ...contactInfo }: LeadFormFields): Promise<void> {
    try {
      const { leadResponse, leadPayload } = await leadSubmitter({
        vehicle: selectedVehicle,
        contactInfo,
        sendAlerts,
      });

      trackLeadSuccess({
        vehicle: selectedVehicle,
        contactInfo,
        zip,
        leadResponse,
        geolocation: leadPayload.geolocation,
        cplValue: selectedVehicle?.cplValue,
      });

      if (onSubmitSuccess) {
        onSubmitSuccess(selectedVehicle);
      }
    } catch (e) {
      console.error(e);
      captureException(e);
      showError(t('errors.generic'), { debug: _.toPlainObject(e) });
      throw new Error('Error submitting lead');
    }
  }

  return (
    <LeadFormTrackingContext.Provider value={{ sourcePage: SourcePages.SrpLeadForm }}>
      <FormManager prefillFromAccount onSubmit={handleSubmit}>
        {({ submissionState }: { submissionState: SubmissionState }): JSX.Element => {
          const isSubmitted =
            leadSubmission.isSubmitted || submissionState === SubmissionState.submitted;

          return (
            <div className="qc-container">
              <hr className="qc-line" />
              <CloseIconButton onClick={onClose} aria-label={t('accessibility.closeModal')} />
              <div className="qc-header-container">
                <img src={qcLogo} className="qc-logo" width={34} height={34} />
                <h3 className="qc-header">{t('leadForm.quickContactTitle')}</h3>
                <p className="qc-description">{t('leadForm.quickContactDescription')}</p>
              </div>
              <div className="qc-form-container">
                <ModalForm
                  submissionState={
                    leadSubmission.isSubmitted ? SubmissionState.submitted : submissionState
                  }
                  isSubmitted={isSubmitted}
                  marginClassName={marginCss.className}
                  showZipCode={true}
                  showAddress={false}
                  showAlerts={false}
                  showMessage={false}
                  showTerms={false}
                  lockEmail={true}
                  submitButtonNormalText={t('leadForm.confirmSendRequest')}
                  submitButtonClassName="qc-submit-button"
                  inputClassName={classNames('padding', inputCss.className)}
                  leadSubmission={leadSubmission}
                />
              </div>
              {marginCss.styles}
              {inputCss.styles}
              <style jsx>{`
                .qc-container {
                  display: flex;
                  align-items: center;
                  flex-direction: column;
                  padding-top: 46px;
                  height: 100%;
                }

                @media (min-width: 850px) {
                  .qc-container {
                    padding-top: 22px;
                  }
                }

                @media (min-width: ${screen.smMin}) {
                  .qc-container {
                    display: flex;
                    align-items: center;
                    flex-direction: column;
                    height: 100%;
                  }
                }

                .qc-line {
                  border-color: #d3d3d3;
                  width: 100%;
                  padding: 0;
                  margin: 0;
                  position: absolute;
                  top: 122px;
                }

                .closeButton {
                  background-image: url(${closeButton});
                  top: 11px;
                  right: 5px;
                  width: 40px;
                  height: 40px;
                  padding: 0;
                  position: absolute;
                  background-size: 16px 16px;
                  background-repeat: no-repeat;
                  background-position: center;
                }

                .qc-header-container {
                  flex-grow: 1;
                  border: 1px solid #d3d3d3;
                  border-radius: 8px;
                  display: flex;
                  justify-content: center;
                  flex-direction: column;
                  align-items: center;
                  padding: 0 15px 17px;
                  background-color: #fff;
                  margin: 0 16px;
                  z-index: 1;
                  box-shadow: 0px 8px 16px rgba(0, 0, 0, 0.08);
                }

                .qc-header {
                  text-align: center;
                  margin: 0;
                  padding: 0;
                  font-size: 22px;
                  margin-bottom: 8px;
                }

                .qc-logo {
                  margin-top: -17px;
                  margin-bottom: 10px;
                }

                .qc-description {
                  text-align: center;
                  font-size: 14px;
                  margin: 0;
                }

                .qc-form-container {
                  width: 100%;
                  height: 100%;
                }
              `}</style>
            </div>
          );
        }}
      </FormManager>
    </LeadFormTrackingContext.Provider>
  );
};

export default QuickContactLeadForm;
